import type { TargetedEvent } from 'preact/compat';
import { useState } from 'preact/hooks';
import InputMask from 'react-input-mask';

import type { I_CustomBirthDateProps } from '@/types/CustomBirthDate';

export const CustomBirthDate = ({ date, onChange, error, onError }: I_CustomBirthDateProps) => {
  const [value, setValue] = useState(date ? toDisplayFormat(date) : '');
  const [touched, setTouched] = useState(false);

  function toDisplayFormat(dateStr: string) {
    const [yyyy, mm, dd] = dateStr.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }

  function toISOFormat(str: string) {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
      return undefined;
    }

    const [ddStr = '', mmStr = '', yyyyStr = ''] = str.split('/');
    const mm = Number(mmStr);
    const dd = Number(ddStr);
    const yyyy = Number(yyyyStr);

    if (mm < 1 || mm > 12) {
      return undefined;
    }

    const daysInMonth = new Date(yyyy, mm, 0).getDate();
    if (dd < 1 || dd > daysInMonth) {
      return undefined;
    }

    const d = new Date(yyyy, mm - 1, dd);
    if (isNaN(d.getTime())) {
      return undefined;
    }

    const today = new Date();
    const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());

    if (d > today || d < minDate) {
      return undefined;
    }

    return `${yyyyStr}-${mmStr.padStart(2, '0')}-${ddStr.padStart(2, '0')}`;
  }

  const handleChange = (e: TargetedEvent<HTMLInputElement, Event>) => {
    const newVal = (e.currentTarget as HTMLInputElement).value;
    setValue(newVal);

    const isoDate = toISOFormat(newVal);
    if (isoDate) {
      onChange(isoDate);
    } else {
      onChange(undefined);
    }
  };

  const handleBlur = () => {
    setTouched(true);

    if (!value?.trim()) {
      onError?.(undefined);
      return;
    }

    const isoDate = toISOFormat(value);
    if (!isoDate) {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        onError?.('Date must be in DD/MM/YYYY format');
      } else {
        const [ddStr, mmStr, yyyyStr] = value.split('/');
        const mm = Number(mmStr);
        const dd = Number(ddStr);
        const yyyy = Number(yyyyStr);

        const today = new Date();
        const inputDate = new Date(yyyy, mm - 1, dd);
        const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());

        if (mm < 1 || mm > 12) {
          onError?.('Month must be between 01 and 12');
        } else {
          const daysInMonth = new Date(yyyy, mm, 0).getDate();
          if (dd < 1 || dd > daysInMonth) {
            onError?.(`Day must be between 01 and ${daysInMonth} for month ${mm}`);
          } else if (inputDate > today) {
            onError?.('Date cannot be in the future');
          } else if (inputDate < minDate) {
            onError?.('Date is too far in the past');
          } else {
            onError?.('Invalid date');
          }
        }
      }
    } else {
      onError?.(undefined);
    }
  };

  return (
    <div className='mt-2 flex flex-col gap-2'>
      <InputMask
        mask='99/99/9999'
        placeholder='DD/MM/YYYY'
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`w-full rounded-md border px-2 py-2 text-sm ${
          touched && error ? 'border-red-500' : 'border-gray-300'
        }`}
        alwaysShowMask={false}
      />
      {touched && value && error && <p className='!text-red-600'>{error}</p>}
    </div>
  );
};
